import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { Member, CheckIn, CheckInValidation } from '../types';
import notificationService from '../services/notificationService';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  memberCheckedIn: {
    checkIn: CheckIn;
    member: Member;
    validation: CheckInValidation;
    timestamp: string;
  } | null;
  clearMemberCheckedIn: () => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  memberCheckedIn: null,
  clearMemberCheckedIn: () => {},
});

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [memberCheckedIn, setMemberCheckedIn] = useState<SocketContextType['memberCheckedIn']>(null);
  const { isAuthenticated, token } = useAuth();

  const clearMemberCheckedIn = useCallback(() => {
    setMemberCheckedIn(null);
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      // Use REACT_APP_SOCKET_URL if set, otherwise use relative path (empty string) for nginx proxy
      const socketUrl = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || '';
      const newSocket = io(socketUrl, {
        auth: {
          token,
        },
      });

      newSocket.on('connect', () => {
        setIsConnected(true);
        console.log('Connected to server');
      });

      newSocket.on('disconnect', () => {
        setIsConnected(false);
        console.log('Disconnected from server');
      });

      // Handle member check-in event
      newSocket.on('member-checked-in', (data) => {
        console.log('Member checked in:', data);
        setMemberCheckedIn(data);
        
        // Use enhanced notification service
        notificationService.showEnhancedCheckInNotification({
          member: data.member,
          validation: data.validation,
          timestamp: data.timestamp
        });
        
        // Auto-clear after 30 seconds
        setTimeout(() => {
          setMemberCheckedIn(null);
        }, 30000);
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
      };
    } else {
      if (socket) {
        socket.close();
        setSocket(null);
        setIsConnected(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  const value: SocketContextType = {
    socket,
    isConnected,
    memberCheckedIn,
    clearMemberCheckedIn
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
